import theRealFetch from 'isomorphic-fetch';
import qs from 'qs';
import { message } from 'antd';
import cloneDeep from 'lodash/cloneDeep';
import cookie from './cookie';
import storage from '@/utils/storage';
import isNil from '@/utils/isNil';
import { longanDispatchError } from '@fx/longan-sdk';

// 过滤longan无需上报的文案
const longanFilterText = ['导出数据不能为空', '参数异常：地理搜索条件不全'];

/** Utils
 -------------------------------------------------- */
const deleteDeep = (target) =>
  Object.keys(target).forEach((key) => {
    if (typeof target[key] === 'object' && target[key] !== null) {
      deleteDeep(target[key]);
    } else if (isNil(target[key])) {
      delete target[key];
    }
  });
const parseParams = (params) => {
  const paramsCopy = cloneDeep(params);
  deleteDeep(paramsCopy);
  return paramsCopy;
};
const formatUrl = (url) => {
  if (typeof url !== 'string') {
    throw new Error('url must be string');
  }
  if (url.includes('//')) {
    return url;
  }
  const newUrl = url[0] === '/' ? url : `/${url}`;
  return newUrl;
};

/** Request
 -------------------------------------------------- */
const defaultOptions = {
  credentials: 'include',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
    isIframe: 'fs',
  },
};

/** Response
 -------------------------------------------------- */
const redirectTo = (url) => {
  const curUrl = encodeURIComponent(window.location.href);
  const { port, hostname } = window.location;
  let passUrl = url.replace('%2FPLACEHOLDER%2F', curUrl);
  if (import.meta.env.DEV) {
    const return_url = encodeURIComponent(`http://${hostname}:${port}/account/setstoken`);
    passUrl = passUrl.replace(/(.*)return_url=.*/, `$1return_url=${return_url}`);
  }
  window.location.replace(passUrl);
};
const redirectMap = {
  110003: (data) => {
    redirectTo(data.url);
    storage.clear();
  },
  110006: (data) => {
    message.error('您的账号被禁用，请重新登录', () => {
      redirectTo(data.url);
      storage.clear();
    });
  },
  110008: () => {
    message.error('您的账号无权限，请联系高级管理员', () => {
      window.location.replace(window.location.href);
    });
  },
};

/**
 * Requests a URL, return a promise.
 *
 * @param {string} url              The URL we want to request
 * @param {string} [type]           The HTTP method for our request
 * @param {object} [params]         The HTTP message body for our request
 * @param {boolean} [notFormat]     Whether format params using `qs`
 * @param {string} [errMsg]         The message we want to show when request got error
 * @param {...any} [otherOptions]   Other options except "method" & "body" for our request
 */
const fetch = (
  url,
  {
    type = 'GET',
    isFile,
    params,
    notFormat = false,
    errMsg,
    useJson,
    noFilterEmpty,
    ...otherOptions
  } = {},
) => {
  let newUrl = formatUrl(url);
  let newOptions = { ...defaultOptions, ...otherOptions, method: type };

  // 灰度店铺添加前缀
  // const shopItem = storage.get('shopItem');
  // if (shopItem && shopItem.isGray) {
  //   newUrl = `/hd${newUrl}`;
  // }

  const hd = cookie.get('hd');
  if (hd) {
    newUrl = `/hd${newUrl}`;
  }

  if (params) {
    if (type === 'GET') {
      if (noFilterEmpty) {
        newUrl = `${newUrl}?${qs.stringify(params)}`;
      } else {
        newUrl = `${newUrl}?${qs.stringify(parseParams(params))}`;
      }
    } else if (type === 'POST') {
      if (useJson) {
        newOptions = {
          ...newOptions,
          body: JSON.stringify(params),
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json;charset=UTF-8',
            isIframe: 'fs',
          },
        };
      } else if (isFile) {
        newOptions = {
          credentials: 'include',
          ...otherOptions,
          method: type,
          body: params,
        };
      } else {
        newOptions = { ...newOptions, body: notFormat ? params : qs.stringify(params) };
      }
    }
  }

  return theRealFetch(newUrl, newOptions)
    .then((res) => {
      const { status, statusText } = res;
      if (status >= 200 && status < 300) return res.json();

      const serverErrMsg = `${errMsg ? `${errMsg}，` : ''}接口 ${status}: ${statusText}`;
      return Promise.reject(new Error(serverErrMsg));
    })
    .then((resData) => {
      const { errno, errmsg, data, logId } = resData;
      if (
        (errmsg.includes('参数错误') ||
          errmsg.includes('参数异常') ||
          errmsg.includes('不能为空')) &&
        !longanFilterText.some((text) => errmsg === text)
      ) {
        try {
          longanDispatchError({
            error_message: '接口错误：' + newUrl,
            error_content: `参数异常${qs.stringify(params || '') || ''}logId=${logId}`,
            error_level: '0', // 错误等级，为0时可以触发北斗报警功能
            error_tag: 'api_err', // 北斗报警标示
            alarm_msg: `接口异常logId=${logId},path=${newUrl}`,
          });
        } catch (error) {
          console.log('longan上报错误', error);
        }
      }
      if (newOptions && newOptions.getAllResponseData) return resData;
      if (errno === 0) return data;
      // 账号未登录、被禁用等
      const redirect = redirectMap[errno];
      if (redirect) {
        // 跳转到登录页面
        return redirect(data);
      }

      const dataErrMsg = `${errMsg ? `${errMsg}，` : ''}${errmsg}`;

      return Promise.reject(new Error(dataErrMsg));
    })
    .catch((err) => {
      message.error(err.message);
    });
};

export default fetch;
